import type { PainterResidualWarp, VerticalShiftField } from '../../render/painterWarp';

// The larger absolute eigenvalue of the symmetric matrix [[a, b], [b, c]].
function largestEigenvalue(a: number, b: number, c: number): number {
  const mean = (a + c) / 2;
  const spread = Math.sqrt(((a - c) / 2) ** 2 + b * b);
  return Math.max(Math.abs(mean + spread), Math.abs(mean - spread));
}

/**
 * The largest second derivative of the shift along any direction at a rendered pixel: the larger
 * absolute eigenvalue of its Hessian, the sum over centres of w (ln r² + 1) I + 2 w d dᵀ / r² in
 * spline units.
 */
export function curvatureAt(warp: PainterResidualWarp, u: number, v: number): number {
  let [huu, huv, hvv] = [0, 0, 0];
  for (let i = 0; i < warp.centresPx.length; i++) {
    const du = (u - warp.centresPx[i]![0]) / warp.scalePx;
    const dv = (v - warp.centresPx[i]![1]) / warp.scalePx;
    const r2 = du * du + dv * dv;
    if (r2 === 0) continue;
    const w = warp.weights[i]!;
    const diagonal = w * (Math.log(r2) + 1);
    huu += diagonal + (2 * w * du * du) / r2;
    hvv += diagonal + (2 * w * dv * dv) / r2;
    huv += (2 * w * du * dv) / r2;
  }
  return largestEigenvalue(huu, huv, hvv) / warp.scalePx ** 2;
}

interface ColumnTerms {
  readonly hu: number;
  readonly hv: number;
  readonly huu: number;
  readonly huv: number;
  readonly hvv: number;
}

// The gradient and Hessian of the column spline h at (u, row), in spline units.
function columnTerms(columns: VerticalShiftField, u: number, row: number): ColumnTerms {
  const s = columns.scalePx;
  let [hu, hv, huu, huv, hvv] = [columns.affine[1], columns.affine[2], 0, 0, 0];
  for (let i = 0; i < columns.centresPx.length; i++) {
    const du = (u - columns.centresPx[i]![0]) / s;
    const dv = (row - columns.centresPx[i]![1]) / s;
    const r2 = du * du + dv * dv;
    if (r2 === 0) continue;
    const w = columns.weights[i]!;
    const diagonal = w * (Math.log(r2) + 1);
    hu += diagonal * du;
    hv += diagonal * dv;
    huu += diagonal + (2 * w * du * du) / r2;
    hvv += diagonal + (2 * w * dv * dv) / r2;
    huv += (2 * w * du * dv) / r2;
  }
  return { hu, hv, huu, huv, hvv };
}

/**
 * The largest second derivative of the column spline L h itself at (u, row), with the lever
 * L = (row - heldRowPx) / s: its Hessian is [[L h_uu, h_u / s + L h_uv], [h_u / s + L h_uv,
 * 2 h_v / s + L h_vv]], the derivatives of h taken as for the row shift.
 */
export function splineCurvature(columns: VerticalShiftField, u: number, row: number): number {
  const s = columns.scalePx;
  const { hu, hv, huu, huv, hvv } = columnTerms(columns, u, row);
  const lever = (row - columns.heldRowPx) / s;
  return largestEigenvalue(lever * huu, hu + lever * huv, 2 * hv + lever * hvv) / s ** 2;
}

/**
 * The largest second derivative of the column shift along any direction at a rendered pixel: the
 * spline's down to the last fitted row, and below it that of the row's shift, which bends along the
 * row alone, by L h_uu. 0 without a column shift.
 */
export function columnCurvatureAt(warp: PainterResidualWarp, u: number, v: number): number {
  const columns = warp.vertical;
  if (!columns) return 0;
  const last = columns.lastFittedRowPx;
  if (v <= last) return splineCurvature(columns, u, v);
  const lever = (last - columns.heldRowPx) / columns.scalePx;
  return Math.abs(lever * columnTerms(columns, u, last).huu) / columns.scalePx ** 2;
}
